import React, { useState, useEffect, useRef } from 'react';
import { useEditObjectDataMutation } from '../../services/Listing';

// ─── CI-Tokens ────────────────────────────────────────────────────────────────
const CYAN         = '#1DB3B3';
const CYAN_HOVER   = '#17A3A3';
const CYAN_FOCUS   = 'rgba(29, 179, 179, 0.15)';
const NEUTRAL_100  = '#EBEBF0';
const NEUTRAL_200  = '#D8D9E0';
const NEUTRAL_300  = '#ACACB8';
const NEUTRAL_400  = '#8C8FA0';
const NEUTRAL_500  = '#6B6E82';
const NEUTRAL_600  = '#4B4E63';
const ERROR_500    = '#E83838';
const SUCCESS_500  = '#1FA534';
const WARNING_50   = '#FCF7E6';
const WARNING_500  = '#F0C045';
const DARK         = '#1F1F1F';

// ─── Translate helpers ────────────────────────────────────────────────────────
const PROPERTY_TYPE_OPTIONS = [
  { value: 'House',     label: 'Haus' },
  { value: 'Apartment', label: 'Wohnung' },
];

const BUILDING_TYPE_OPTIONS = [
  { value: 'Einfamilienhaus',  label: 'Einfamilienhaus' },
  { value: 'Mehrfamilienhaus', label: 'Mehrfamilienhaus' },
  { value: 'Doppelhaus',       label: 'Doppelhaus' },
  { value: 'Reihenhaus',       label: 'Reihenhaus' },
  { value: 'Bungalow',         label: 'Bungalow' },
];

const CONDITION_OPTIONS = [
  { value: 'Neuwertig',         label: 'Neuwertig' },
  { value: 'Gepflegt',          label: 'Gepflegt' },
  { value: 'Renovierungsbedarf',label: 'Renovierungsbedarf' },
  { value: 'Sanierungsbedarf',  label: 'Sanierungsbedarf' },
];

const EQUIPMENT_OPTIONS = [
  { value: 'Einfach', label: 'Einfach' },
  { value: 'Normal',  label: 'Normal' },
  { value: 'Gehoben', label: 'Gehoben' },
  { value: 'Luxus',   label: 'Luxus' },
];

// ─── Inline styles (CI-konform) ───────────────────────────────────────────────

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1050,
};

const modalBoxStyle = {
  background: '#FFFFFF',
  borderRadius: 12,
  boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.18)',
  width: 720,
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const modalHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px 24px 20px',
  borderBottom: `1px solid ${NEUTRAL_100}`,
  flexShrink: 0,
};

const modalBodyStyle = {
  padding: '20px 24px 24px',
  overflowY: 'auto',
  flex: 1,
  scrollbarWidth: 'thin',
};

const modalFooterStyle = {
  padding: '14px 24px',
  borderTop: `1px solid ${NEUTRAL_100}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
};

function labelStyle() {
  return {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: NEUTRAL_500,
    marginBottom: 4,
  };
}

function inputStyle(hasError = false, focused = false) {
  return {
    width: '100%',
    height: 34,
    padding: '0 10px',
    border: hasError
      ? `1.5px solid ${ERROR_500}`
      : focused
        ? `1.5px solid ${CYAN}`
        : `1px solid ${NEUTRAL_200}`,
    borderRadius: 8,
    fontSize: 14,
    color: DARK,
    outline: 'none',
    boxShadow: focused ? `0 0 0 3px ${CYAN_FOCUS}` : 'none',
    boxSizing: 'border-box',
    transition: 'border 150ms, box-shadow 150ms',
    background: '#FFFFFF',
    fontFamily: 'inherit',
  };
}

function suffixInputWrapStyle() {
  return { position: 'relative', display: 'flex', alignItems: 'center' };
}

// ─── Small reusable field components ─────────────────────────────────────────

function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom: 0 }}>
      {label && <label style={labelStyle()}>{label}</label>}
      {children}
      {error && (
        <div style={{ fontSize: 12, color: ERROR_500, marginTop: 4 }}>{error}</div>
      )}
    </div>
  );
}

function TextInput({ value, onChange, onBlur, placeholder, style: extraStyle, hasError, suffix, type = 'text' }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={suffix ? suffixInputWrapStyle() : {}}>
      <input
        type={type}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); onBlur?.(); }}
        placeholder={placeholder}
        style={{ ...inputStyle(hasError, focused), ...(suffix ? { paddingRight: 32 } : {}), ...extraStyle }}
      />
      {suffix && (
        <span style={{
          position: 'absolute', right: 10, fontSize: 12,
          color: NEUTRAL_400, pointerEvents: 'none',
        }}>{suffix}</span>
      )}
    </div>
  );
}

function SelectInput({ value, onChange, onBlur, options, placeholder, hasError }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value || null)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); onBlur?.(); }}
        style={{
          ...inputStyle(hasError, focused),
          appearance: 'none',
          WebkitAppearance: 'none',
          paddingRight: 28,
          cursor: 'pointer',
        }}
      >
        <option value="">{placeholder || 'Bitte wählen'}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {/* Chevron */}
      <span style={{
        position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)',
        pointerEvents: 'none', color: NEUTRAL_400,
      }}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </span>
    </div>
  );
}

function SectionSeparator({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <span style={{
        fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: 1, color: NEUTRAL_400, whiteSpace: 'nowrap',
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: NEUTRAL_100 }} />
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ type, message, onHide }) {
  useEffect(() => {
    const t = setTimeout(onHide, 2800);
    return () => clearTimeout(t);
  }, [onHide]);

  const isError = type === 'error';
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
      background: isError ? '#FDEAEA' : DARK,
      border: isError ? `1px solid ${ERROR_500}` : 'none',
      borderRadius: 8, padding: '12px 16px',
      display: 'flex', alignItems: 'center', gap: 8,
      animation: 'slideUpFadeIn 200ms ease forwards',
    }}>
      {!isError && (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke={SUCCESS_500} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
      <span style={{ fontSize: 14, fontWeight: 500, color: isError ? DARK : '#FFFFFF' }}>
        {message}
      </span>
    </div>
  );
}

// ─── Main Modal Component ─────────────────────────────────────────────────────

/**
 * EditObjectModal
 *
 * Props:
 *   deal     — mapped listing object (from mapListing)
 *   raw      — raw listing object from API (for fallback fields)
 *   open     — boolean
 *   onClose  — () => void
 *   onSave   — (updatedListing) => void  called after successful save
 */
export default function EditObjectModal({ deal, raw, open, onClose, onSave }) {
  const [editObjectData, { isLoading: isSaving }] = useEditObjectDataMutation();

  // Form state
  const initialValues = () => ({
    CustomName:          deal?.customName ?? '',
    PropertyType:        raw?.PropertyType || deal?.propertyType || '',
    Street:              raw?.Street || deal?.street || '',
    HouseNumber:         raw?.HouseNumber || deal?.houseNumber || '',
    PostalCode:          raw?.PostalCode || deal?.postalCode || '',
    City:                raw?.City || deal?.city?.split(',')[0]?.trim() || '',
    District:            raw?.District || deal?.district || '',
    State:               raw?.State || deal?.state || '',
    UsableArea:          raw?.UsableArea != null ? String(raw.UsableArea) : (deal?.usableArea != null ? String(deal.usableArea) : ''),
    LandArea:            raw?.LandArea != null ? String(raw.LandArea) : (deal?.landArea != null ? String(deal.landArea) : ''),
    Rooms:               raw?.Rooms != null ? String(raw.Rooms) : (deal?.rooms != null ? String(deal.rooms) : ''),
    Bedrooms:            raw?.Bedrooms != null ? String(raw.Bedrooms) : (deal?.bedrooms != null ? String(deal.bedrooms) : ''),
    Bathrooms:           raw?.Bathrooms != null ? String(raw.Bathrooms) : (deal?.bathrooms != null ? String(deal.bathrooms) : ''),
    NumberOfFloors:      raw?.NumberOfFloors != null ? String(raw.NumberOfFloors) : (deal?.numberOfFloors != null ? String(deal.numberOfFloors) : ''),
    BuildingType:        raw?.BuildingType || deal?.buildingType || '',
    PropertyCondition:   raw?.PropertyCondition || deal?.propertyCondition || '',
    QualityOfEquipment:  raw?.QualityOfEquipment || deal?.qualityOfEquipment || '',
    Renovation:          raw?.Renovation || deal?.renovation || '',
  });

  const [values, setValues]       = useState(initialValues);
  const [errors, setErrors]       = useState({});
  const [isDirty, setIsDirty]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast]         = useState(null);
  const overlayRef                = useRef(null);

  // Re-init when deal changes or modal opens
  useEffect(() => {
    if (open) {
      setValues(initialValues());
      setErrors({});
      setIsDirty(false);
      setShowConfirm(false);
    }
  }, [open, deal?.id]);

  // Keyboard: Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, isDirty]);

  if (!open) return null;

  function setField(field, value) {
    setValues(v => ({ ...v, [field]: value }));
    setIsDirty(true);
    // Clear error on change
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
  }

  function handleClose() {
    if (isDirty) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  }

  function handleDiscard() {
    setShowConfirm(false);
    onClose();
  }

  function validate() {
    const newErrors = {};
    if (!values.PropertyType) newErrors.PropertyType = 'Bitte wähle einen Objekttyp.';
    if (!values.Street || values.Street.trim().length < 2) newErrors.Street = 'Bitte gib eine Straße an.';
    if (!values.HouseNumber || values.HouseNumber.trim().length < 1) newErrors.HouseNumber = 'Bitte gib eine Hausnummer an.';
    if (!values.PostalCode || !/^\d{5}$/.test(values.PostalCode.trim())) newErrors.PostalCode = 'Bitte gib eine gültige PLZ an (5 Ziffern).';
    if (!values.City || values.City.trim().length < 2) newErrors.City = 'Bitte gib eine Stadt an.';
    return newErrors;
  }

  async function handleSave() {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build payload — only non-empty optional values (but always include mandatory)
    const payload = { listingId: deal.id };
    const allFields = [
      'CustomName', 'PropertyType',
      'Street', 'HouseNumber', 'PostalCode', 'City', 'District', 'State',
      'UsableArea', 'LandArea', 'Rooms', 'Bedrooms', 'Bathrooms',
      'NumberOfFloors', 'BuildingType', 'PropertyCondition', 'QualityOfEquipment', 'Renovation',
    ];
    for (const field of allFields) {
      const val = values[field];
      // Number fields: parse; empty string → null
      if (['UsableArea', 'LandArea', 'Rooms', 'Bedrooms', 'Bathrooms', 'NumberOfFloors'].includes(field)) {
        payload[field] = val !== '' && val != null ? parseFloat(val) : null;
      } else {
        payload[field] = val !== '' ? val : null;
      }
    }

    try {
      const result = await editObjectData(payload).unwrap();
      setIsDirty(false);
      setToast({ type: 'success', message: 'Gespeichert' });
      onSave?.(result?.listing || payload);
      onClose();
    } catch {
      setToast({ type: 'error', message: 'Speichern fehlgeschlagen. Bitte erneut versuchen.' });
    }
  }

  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) {
      handleClose();
    }
  }

  const blurValidate = (field) => () => {
    const newErrors = validate();
    if (newErrors[field]) {
      setErrors(e => ({ ...e, [field]: newErrors[field] }));
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes slideUpFadeIn {
          from { transform: translateY(8px); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
        .edit-modal-overlay { animation: fadeIn 180ms ease; }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .edit-modal-box { animation: modalIn 180ms ease; }
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        /* Responsive: auf schmalen Viewports einspaltig */
        @media (max-width: 767px) {
          .edit-modal-columns { flex-direction: column !important; }
          .edit-modal-divider  { display: none !important; }
          .edit-modal-col      { padding-right: 0 !important; }
        }
      `}</style>

      <div
        ref={overlayRef}
        className="edit-modal-overlay"
        style={modalOverlayStyle}
        onClick={handleOverlayClick}
        aria-modal="true"
        role="dialog"
        aria-label="Objektdaten bearbeiten"
      >
        <div className="edit-modal-box" style={modalBoxStyle}>

          {/* ── Header ── */}
          <div style={modalHeaderStyle}>
            <span style={{ fontSize: 20, fontWeight: 600, color: DARK }}>
              Objektdaten bearbeiten
            </span>
            <button
              onClick={handleClose}
              aria-label="Schließen"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: NEUTRAL_400, borderRadius: 6, padding: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 150ms, color 150ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F5F5F8'; e.currentTarget.style.color = NEUTRAL_600; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = NEUTRAL_400; }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4.5 4.5L13.5 13.5M13.5 4.5L4.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* ── Body ── */}
          <div style={modalBodyStyle}>
            <div
              className="edit-modal-columns"
              style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}
            >
              {/* Left column: Identifikation + Adresse */}
              <div className="edit-modal-col" style={{ flex: 1, paddingRight: 24 }}>

                {/* IDENTIFIKATION */}
                <SectionSeparator label="Identifikation" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Field label="Objektname">
                    <TextInput
                      value={values.CustomName}
                      onChange={v => setField('CustomName', v)}
                      placeholder="z.B. MFH Hauptstraße 12"
                    />
                    <div style={{ fontSize: 11, color: NEUTRAL_400, marginTop: 4 }}>
                      Eigener Name für die Übersicht. Überschreibt die automatische Adressanzeige.
                    </div>
                  </Field>
                  <Field label="Objekttyp" error={errors.PropertyType}>
                    <SelectInput
                      value={values.PropertyType}
                      onChange={v => setField('PropertyType', v)}
                      onBlur={blurValidate('PropertyType')}
                      options={PROPERTY_TYPE_OPTIONS}
                      placeholder="Bitte wählen"
                      hasError={!!errors.PropertyType}
                    />
                  </Field>
                </div>

                {/* ADRESSE */}
                <div style={{ marginTop: 16 }}>
                  <SectionSeparator label="Adresse" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {/* Straße + Hausnummer */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 8 }}>
                      <Field label="Straße" error={errors.Street}>
                        <TextInput
                          value={values.Street}
                          onChange={v => setField('Street', v)}
                          onBlur={blurValidate('Street')}
                          placeholder="Musterstraße"
                          hasError={!!errors.Street}
                        />
                      </Field>
                      <Field label="Nr." error={errors.HouseNumber}>
                        <TextInput
                          value={values.HouseNumber}
                          onChange={v => setField('HouseNumber', v)}
                          onBlur={blurValidate('HouseNumber')}
                          placeholder="12"
                          hasError={!!errors.HouseNumber}
                        />
                      </Field>
                    </div>
                    {/* PLZ + Stadt */}
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 8 }}>
                      <Field label="PLZ" error={errors.PostalCode}>
                        <TextInput
                          value={values.PostalCode}
                          onChange={v => setField('PostalCode', v)}
                          onBlur={blurValidate('PostalCode')}
                          placeholder="63110"
                          hasError={!!errors.PostalCode}
                        />
                      </Field>
                      <Field label="Stadt" error={errors.City}>
                        <TextInput
                          value={values.City}
                          onChange={v => setField('City', v)}
                          onBlur={blurValidate('City')}
                          placeholder="Rodgau"
                          hasError={!!errors.City}
                        />
                      </Field>
                    </div>
                    <Field label="Stadtteil">
                      <TextInput
                        value={values.District}
                        onChange={v => setField('District', v)}
                        placeholder="Kernstadt"
                      />
                    </Field>
                    <Field label="Bundesland">
                      <TextInput
                        value={values.State}
                        onChange={v => setField('State', v)}
                        placeholder="Hessen"
                      />
                    </Field>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div
                className="edit-modal-divider"
                style={{ width: 1, background: NEUTRAL_100, alignSelf: 'stretch', flexShrink: 0 }}
              />

              {/* Right column: Gebäude-Details */}
              <div style={{ flex: 1, paddingLeft: 24 }}>
                <SectionSeparator label="Gebäude-Details" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Zeile 1: Nutzfläche / Grundstücksfläche */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="Nutzfläche">
                      <TextInput
                        type="number"
                        value={values.UsableArea}
                        onChange={v => setField('UsableArea', v)}
                        placeholder="0"
                        suffix="m²"
                      />
                    </Field>
                    <Field label="Grundstück">
                      <TextInput
                        type="number"
                        value={values.LandArea}
                        onChange={v => setField('LandArea', v)}
                        placeholder="0"
                        suffix="m²"
                      />
                    </Field>
                  </div>

                  {/* Zeile 2: Zimmer / Schlafzimmer / Badezimmer (3-spaltig) */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <Field label="Zimmer">
                      <TextInput
                        type="number"
                        value={values.Rooms}
                        onChange={v => setField('Rooms', v)}
                        placeholder="—"
                      />
                    </Field>
                    <Field label="Schlafzi.">
                      <TextInput
                        type="number"
                        value={values.Bedrooms}
                        onChange={v => setField('Bedrooms', v)}
                        placeholder="—"
                      />
                    </Field>
                    <Field label="Bad">
                      <TextInput
                        type="number"
                        value={values.Bathrooms}
                        onChange={v => setField('Bathrooms', v)}
                        placeholder="—"
                      />
                    </Field>
                  </div>

                  {/* Zeile 3: Geschosse / Gebäudetyp */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="Geschosse">
                      <TextInput
                        type="number"
                        value={values.NumberOfFloors}
                        onChange={v => setField('NumberOfFloors', v)}
                        placeholder="—"
                      />
                    </Field>
                    <Field label="Gebäudetyp">
                      <SelectInput
                        value={values.BuildingType}
                        onChange={v => setField('BuildingType', v)}
                        options={BUILDING_TYPE_OPTIONS}
                        placeholder="Bitte wählen"
                      />
                    </Field>
                  </div>

                  {/* Zeile 4: Zustand / Ausstattung */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="Zustand">
                      <SelectInput
                        value={values.PropertyCondition}
                        onChange={v => setField('PropertyCondition', v)}
                        options={CONDITION_OPTIONS}
                        placeholder="Bitte wählen"
                      />
                    </Field>
                    <Field label="Ausstattung">
                      <SelectInput
                        value={values.QualityOfEquipment}
                        onChange={v => setField('QualityOfEquipment', v)}
                        options={EQUIPMENT_OPTIONS}
                        placeholder="Bitte wählen"
                      />
                    </Field>
                  </div>

                  {/* Zeile 5: Renovierung (volle Breite) */}
                  <Field label="Renovierung">
                    <TextInput
                      value={values.Renovation}
                      onChange={v => setField('Renovation', v)}
                      placeholder="z.B. Bad 2020, Dach 2018"
                    />
                  </Field>
                </div>
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div style={{
            ...modalFooterStyle,
            flexDirection: 'column',
            padding: 0,
          }}>
            {/* Confirmation hint (ungespeicherte Änderungen) */}
            {showConfirm && (
              <div style={{
                background: WARNING_50,
                borderTop: `1px solid ${WARNING_500}`,
                padding: '12px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                boxSizing: 'border-box',
              }}>
                <span style={{ fontSize: 14, color: DARK }}>Änderungen verwerfen?</span>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={() => setShowConfirm(false)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 14, color: NEUTRAL_600, padding: '4px 8px',
                      borderRadius: 6,
                    }}
                  >
                    Weiter bearbeiten
                  </button>
                  <button
                    onClick={handleDiscard}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 14, color: ERROR_500, fontWeight: 600, padding: '4px 8px',
                      borderRadius: 6,
                    }}
                  >
                    Ja, verwerfen
                  </button>
                </div>
              </div>
            )}

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 24px', width: '100%', boxSizing: 'border-box',
            }}>
              <button
                onClick={handleClose}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 14, fontWeight: 600, color: NEUTRAL_600,
                  height: 38, padding: '0 16px', borderRadius: 8,
                  transition: 'background 150ms',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#F5F5F8'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  background: isSaving ? NEUTRAL_200 : CYAN,
                  border: 'none', cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontSize: 14, fontWeight: 600, color: '#FFFFFF',
                  height: 38, padding: '0 16px', borderRadius: 8,
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'background 150ms',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={e => { if (!isSaving) e.currentTarget.style.background = CYAN_HOVER; }}
                onMouseLeave={e => { if (!isSaving) e.currentTarget.style.background = CYAN; }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {isSaving ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onHide={() => setToast(null)}
        />
      )}
    </>
  );
}
